import { useMachine } from "@xstate/react";
import { Plus, X } from "lucide-react";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { EventObject, MetaObject, SetupTypes } from "xstate";
import { assign, setup } from "xstate";

import { MAX_MANUAL_HOLDINGS } from "@lattice/core-lib/types/portfolio/manual-holding.types";
import { getStockLogoUrl } from "@lattice/core-lib/utils/stock-logo";
import {
  portfolioDraftMachine,
  type T_PortfolioDraft,
  type T_PortfolioDraftHolding,
  type T_PortfolioImportResult,
  type T_PortfolioImportSource,
  type T_PortfolioInitialImport,
} from "@lattice/shared-frontend/portfolio/portfolio-draft-machine";

import { Button } from "@lattice/frontend/components/kit/Button";
import { IconButton } from "@lattice/frontend/components/kit/IconButton";
import { ListRow } from "@lattice/frontend/components/kit/List";
import { Sheet } from "@lattice/frontend/components/kit/Sheet";
import StockSearch from "@lattice/frontend/components/workspace/stock-search";
import { TextField } from "@lattice/frontend/components/kit/TextField";
import { trpcApi } from "@lattice/frontend/lib/trpc/client";

import styles from "./ManualHoldingsPanel.module.css";
import {
  SettingsPanelError,
  SettingsPanelLoading,
  SettingsPanelSuccess,
  SettingsSection,
} from "./SettingsPanelState";

function parsePositiveNumber(value: string): number | null {
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

function parseBalance(value: string): number | null {
  if (value.trim().length === 0) return 0;
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
}

function StockLogo({ symbol }: { symbol: string }) {
  const source = getStockLogoUrl(symbol);
  return (
    <span className={styles.stockLogo}>
      <span aria-hidden>{symbol.slice(0, 1)}</span>
      <img
        alt=""
        onError={(event) => {
          event.currentTarget.hidden = true;
        }}
        src={source}
      />
    </span>
  );
}

type T_HoldingEditorContext = {
  holding: T_PortfolioDraftHolding | null;
};

type T_HoldingEditorEvent =
  | { type: "CLOSE" }
  | { type: "OPEN_ADD" }
  | { holding: T_PortfolioDraftHolding; type: "OPEN_EDIT" };

const holdingEditorTypes: SetupTypes<
  T_HoldingEditorContext,
  T_HoldingEditorEvent,
  Record<never, string>,
  string,
  unknown,
  unknown,
  EventObject,
  MetaObject
> = {};

const holdingEditorMachine = setup({
  types: holdingEditorTypes,
  actions: {
    clearHolding: assign({ holding: null }),
    selectHolding: assign({
      holding: ({ event }) =>
        event.type === "OPEN_EDIT" ? event.holding : null,
    }),
  },
}).createMachine({
  id: "holdingEditor",
  context: { holding: null },
  initial: "closed",
  states: {
    closed: {
      entry: "clearHolding",
      on: {
        OPEN_ADD: { target: "adding" },
        OPEN_EDIT: { actions: "selectHolding", target: "editing" },
      },
    },
    adding: { on: { CLOSE: { target: "closed" } } },
    editing: { on: { CLOSE: { target: "closed" } } },
  },
});

export function ManualHoldingEditorForm({
  disabled,
  initialHolding = null,
  onSubmit,
}: {
  disabled: boolean;
  initialHolding?: T_PortfolioDraftHolding | null;
  onSubmit: (holding: T_PortfolioDraftHolding) => void;
}) {
  const [units, setUnits] = useState(() =>
    initialHolding ? String(initialHolding.units) : "",
  );
  const [selected, setSelected] = useState<{
    image: string | null;
    isCrypto: boolean;
    name: string | null;
    symbol: string;
  } | null>(() =>
    initialHolding
      ? {
          image: null,
          isCrypto: initialHolding.isCrypto,
          name: null,
          symbol: initialHolding.symbol,
        }
      : null,
  );
  const parsedUnits = parsePositiveNumber(units);
  const editing = initialHolding !== null;

  return (
    <div className={styles.holdingEditor}>
      <StockSearch
        appearance="fill"
        onClearSelectedItems={() => {
          setSelected(null);
          if (!editing) setUnits("");
        }}
        onSelect={(stock) =>
          setSelected({
            image: stock.image,
            isCrypto: stock.is_crypto,
            name: stock.name,
            symbol: stock.symbol,
          })
        }
        placeholder="Search ticker or company"
        selectedItems={
          selected
            ? [
                {
                  image: selected.image,
                  label: selected.symbol,
                  value: selected.symbol,
                },
              ]
            : []
        }
        selectionMode="single"
      />
      {selected ? (
        <>
          <TextField
            error={
              units.length > 0 && parsedUnits === null
                ? "Enter a positive number."
                : undefined
            }
            label="Shares or units"
            onChange={(event) => setUnits(event.target.value)}
            placeholder="0"
            value={units}
          />
          <Button
            disabled={disabled || parsedUnits === null}
            label={editing ? "Save changes" : "Add to draft"}
            onClick={() => {
              if (parsedUnits === null) return;
              onSubmit({
                isCrypto: selected.isCrypto,
                symbol: selected.symbol,
                units: parsedUnits,
              });
              setSelected(null);
              setUnits("");
            }}
          />
        </>
      ) : null}
    </div>
  );
}

export type T_ManualHoldingsPanelViewProps = {
  beforeHoldings?: React.ReactNode;
  importRequest?: {
    id: number;
    source: T_PortfolioImportSource;
  } | null;
  initialDraft: T_PortfolioDraft;
  initialImport?: T_PortfolioInitialImport | null;
  importHoldings?: (
    source: T_PortfolioImportSource,
    signal: AbortSignal,
  ) => Promise<T_PortfolioImportResult>;
  intro?: string;
  notice?: React.ReactNode;
  onCancelImport?: () => void;
  onImportCancelled?: (source: T_PortfolioImportSource) => void;
  onImportFailed?: (source: T_PortfolioImportSource) => void;
  onImported?: (result: T_PortfolioImportResult) => void;
  onSave: (draft: T_PortfolioDraft) => Promise<void>;
  onSaved?: (draft: T_PortfolioDraft) => void;
  renderActions?: (primaryAction: React.ReactNode) => React.ReactNode;
  renderAddHoldingForm?: (args: {
    disabled: boolean;
    onAdd: (holding: T_PortfolioDraftHolding) => void;
  }) => React.ReactNode;
  saved?: boolean;
  saveLabel?: string;
};

const cancelledImport = async (): Promise<T_PortfolioImportResult> => ({
  status: "cancelled",
});
const ignoreImportSource = (_source: T_PortfolioImportSource): void =>
  undefined;
const ignoreImportResult = (_result: T_PortfolioImportResult): void =>
  undefined;
const ignoreSavedDraft = (_draft: T_PortfolioDraft): void => undefined;

export function ManualHoldingsPanelView({
  beforeHoldings,
  importRequest = null,
  initialDraft,
  initialImport = null,
  importHoldings = cancelledImport,
  intro = "Changes stay in this draft until you save the complete portfolio.",
  notice,
  onCancelImport,
  onImportCancelled = ignoreImportSource,
  onImportFailed = ignoreImportSource,
  onImported = ignoreImportResult,
  onSave,
  onSaved = ignoreSavedDraft,
  renderActions,
  renderAddHoldingForm,
  saved = false,
  saveLabel = "Save portfolio",
}: T_ManualHoldingsPanelViewProps) {
  const machineInput = useMemo(
    () => ({
      importHoldings,
      initialDraft,
      initialImport,
      onImportCancelled,
      onImportFailed,
      onImported,
      onSaved,
      saveDraft: onSave,
    }),
    [
      importHoldings,
      initialDraft,
      initialImport,
      onImportCancelled,
      onImportFailed,
      onImported,
      onSave,
      onSaved,
    ],
  );
  const [snapshot, send] = useMachine(portfolioDraftMachine, {
    input: machineInput,
  });
  const [editorSnapshot, sendEditor] = useMachine(holdingEditorMachine);
  const handledImportRequestIdRef = useRef<number | null>(null);
  // eslint-disable-next-line no-restricted-syntax -- External import requests are forwarded into the portfolio machine once.
  useEffect(() => {
    if (
      importRequest === null ||
      handledImportRequestIdRef.current === importRequest.id
    ) {
      return;
    }
    handledImportRequestIdRef.current = importRequest.id;
    send({ type: "OPEN_IMPORT" });
    send({ source: importRequest.source, type: "IMPORT" });
  }, [importRequest, send]);
  const editorOpen = !editorSnapshot.matches("closed");
  const editingHolding = editorSnapshot.context.holding;
  const saving = snapshot.matches("saving");
  const cashError = parseBalance(snapshot.context.cashInput) === null;
  const liabilityError = parseBalance(snapshot.context.liabilityInput) === null;
  const primaryAction = (
    <Button
      disabled={cashError || liabilityError}
      label={saveLabel}
      loading={saving}
      onClick={() => send({ type: "SAVE" })}
    />
  );

  if (snapshot.matches("importing")) {
    return (
      <div className={styles.form}>
        <SettingsPanelLoading label="Reading your holdings" />
        <p className={styles.body}>
          We are building editable draft rows. Nothing is saved yet.
        </p>
        <Button
          label="Cancel import"
          onClick={() => {
            send({ type: "CLOSE_IMPORT" });
            onCancelImport?.();
          }}
          variant="quiet"
        />
      </div>
    );
  }

  if (snapshot.matches("importFailed")) {
    return (
      <div className={styles.form}>
        <SettingsPanelError
          message={snapshot.context.error ?? "Holdings could not be imported."}
        />
        <Button
          label="Try again"
          onClick={() => send({ type: "RETRY_IMPORT" })}
        />
        {onCancelImport ? (
          <Button
            label="Choose another option"
            onClick={() => {
              send({ type: "CLOSE_IMPORT" });
              onCancelImport();
            }}
            variant="quiet"
          />
        ) : null}
      </div>
    );
  }

  return (
    <div className={styles.form}>
      {intro ? <p className={styles.body}>{intro}</p> : null}
      {notice}
      {beforeHoldings}

      <SettingsSection
        action={
          <IconButton
            ariaLabel={editorOpen ? "Close holding editor" : "Add holding"}
            disabled={
              !editorOpen &&
              snapshot.context.holdings.length >= MAX_MANUAL_HOLDINGS
            }
            icon={
              <Plus
                aria-hidden
                className={`${styles.addIcon}${
                  editorOpen ? ` ${styles.addIconOpen}` : ""
                }`}
              />
            }
            onClick={() => {
              if (editorOpen) {
                sendEditor({ type: "CLOSE" });
                return;
              }
              sendEditor({ type: "OPEN_ADD" });
            }}
            size="compact"
            variant="quiet"
          />
        }
        label="Portfolio draft"
      >
        {snapshot.context.holdings.length === 0 ? (
          <p className={styles.body}>No manual holdings yet.</p>
        ) : (
          <div className={styles.holdingsList}>
            {snapshot.context.holdings.map((holding) => (
              <ListRow
                ariaLabel={`Edit ${holding.symbol}`}
                key={holding.symbol}
                leading={<StockLogo symbol={holding.symbol} />}
                onClick={() => sendEditor({ holding, type: "OPEN_EDIT" })}
                subtitle={`${holding.units} ${holding.units === 1 ? "unit" : "units"}`}
                title={holding.symbol}
              />
            ))}
          </div>
        )}
      </SettingsSection>

      {editorOpen ? (
        <Sheet
          onClose={() => sendEditor({ type: "CLOSE" })}
          panelStyle={{
            height: 520,
            maxHeight: "calc(100vh - 48px)",
            maxWidth: 480,
            overflow: "visible",
          }}
          title={
            editingHolding ? `Edit ${editingHolding.symbol}` : "Add holding"
          }
          visible
        >
          <div className={styles.holdingSheet}>
            <div className={styles.sheetHeader}>
              <h2>
                {editingHolding
                  ? `Edit ${editingHolding.symbol}`
                  : "Add holding"}
              </h2>
              <IconButton
                ariaLabel="Close holding editor"
                icon={<X aria-hidden />}
                onClick={() => sendEditor({ type: "CLOSE" })}
                size="compact"
                variant="quiet"
              />
            </div>
            {editorSnapshot.matches("adding") && renderAddHoldingForm ? (
              renderAddHoldingForm({
                disabled:
                  snapshot.context.holdings.length >= MAX_MANUAL_HOLDINGS,
                onAdd: (holding) => {
                  send({ holding, type: "UPSERT_HOLDING" });
                  sendEditor({ type: "CLOSE" });
                },
              })
            ) : (
              <ManualHoldingEditorForm
                disabled={
                  editorSnapshot.matches("adding") &&
                  snapshot.context.holdings.length >= MAX_MANUAL_HOLDINGS
                }
                initialHolding={editingHolding}
                key={editingHolding?.symbol ?? "new"}
                onSubmit={(holding) => {
                  if (
                    editingHolding &&
                    editingHolding.symbol !== holding.symbol
                  ) {
                    send({
                      symbol: editingHolding.symbol,
                      type: "REMOVE_HOLDING",
                    });
                  }
                  send({ holding, type: "UPSERT_HOLDING" });
                  sendEditor({ type: "CLOSE" });
                }}
              />
            )}
            {editingHolding ? (
              <Button
                ariaLabel={`Remove ${editingHolding.symbol}`}
                label="Remove holding"
                onClick={() => {
                  send({
                    symbol: editingHolding.symbol,
                    type: "REMOVE_HOLDING",
                  });
                  sendEditor({ type: "CLOSE" });
                }}
                variant="quiet"
              />
            ) : null}
          </div>
        </Sheet>
      ) : null}

      <SettingsSection label="Cash and liabilities">
        <div className={styles.balanceGrid}>
          <TextField
            error={cashError ? "Enter zero or a positive amount." : undefined}
            label="Cash"
            onChange={(event) =>
              send({ type: "UPDATE_CASH", value: event.target.value })
            }
            placeholder="0"
            value={snapshot.context.cashInput}
          />
          <TextField
            error={
              liabilityError ? "Enter zero or a positive amount." : undefined
            }
            label="Liabilities"
            onChange={(event) =>
              send({ type: "UPDATE_LIABILITY", value: event.target.value })
            }
            placeholder="0"
            value={snapshot.context.liabilityInput}
          />
        </div>
      </SettingsSection>

      {snapshot.matches("saveFailed") ? (
        <SettingsPanelError
          message={snapshot.context.error ?? "Portfolio could not be saved."}
        />
      ) : null}
      {saved ? (
        <SettingsPanelSuccess message="Manual portfolio saved." />
      ) : null}
      {renderActions ? renderActions(primaryAction) : primaryAction}
    </div>
  );
}

export function ManualHoldingsPanel() {
  const utils = trpcApi.useUtils();
  const holdings = trpcApi.manual_holdings.list.useQuery(undefined, {
    refetchOnWindowFocus: false,
    staleTime: 2 * 60 * 1000,
  });
  const balance = trpcApi.manual_holdings.getBalance.useQuery(undefined, {
    refetchOnWindowFocus: false,
    staleTime: 2 * 60 * 1000,
  });
  const replaceDraft = trpcApi.manual_holdings.replaceDraft.useMutation();
  const saveDraft = useCallback(
    async (draft: T_PortfolioDraft) => {
      await replaceDraft.mutateAsync({
        cash_balance: draft.cashBalance,
        holdings: draft.holdings.map((holding) => ({
          is_crypto: holding.isCrypto,
          symbol: holding.symbol,
          units: holding.units,
        })),
        liability_balance: draft.liabilityBalance,
      });
      await Promise.all([
        utils.manual_holdings.list.invalidate(),
        utils.manual_holdings.getBalance.invalidate(),
        utils.snaptrade.getAccounts.invalidate(),
        utils.snaptrade.getAccountValueHistory.invalidate(),
      ]);
    },
    [replaceDraft, utils],
  );

  if (holdings.isPending || balance.isPending) {
    return <SettingsPanelLoading label="Loading manual holdings" />;
  }
  if (holdings.isError || balance.isError || !holdings.data || !balance.data) {
    return (
      <SettingsPanelError message="Manual holdings could not be loaded." />
    );
  }

  const initialDraft: T_PortfolioDraft = {
    cashBalance: balance.data.cash_balance,
    holdings: holdings.data.map((holding) => ({
      isCrypto: holding.is_crypto,
      symbol: holding.symbol,
      units: holding.units,
    })),
    liabilityBalance: balance.data.liability_balance,
  };

  return (
    <ManualHoldingsPanelView
      key={`${balance.data.updated_at?.toISOString() ?? "empty"}:${holdings.data.map((holding) => holding.updated_at.toISOString()).join(",")}`}
      initialDraft={initialDraft}
      onSave={saveDraft}
      saved={replaceDraft.isSuccess}
    />
  );
}
